import { useTheme } from '../../contexts/ThemeContext';

// Hook for getting theme-aware classes
export const useThemeClasses = () => {
  const { currentTheme } = useTheme();
  
  return {
    // Background classes
    backgrounds: {
      primary: 'bg-primary-500',
      primaryLight: 'bg-primary-50',
      primaryMuted: 'bg-primary-100',
      secondary: 'bg-secondary-500',
      secondaryLight: 'bg-secondary-50',
      secondaryMuted: 'bg-secondary-100',
      neutral: 'bg-neutral-50',
      neutralCard: 'bg-white/70 backdrop-blur-sm',
      gradient: 'bg-gradient-to-br from-primary-50 via-secondary-25 to-neutral-50',
      gradientPrimary: 'bg-gradient-to-r from-primary-600 to-secondary-600',
      gradientCard: 'bg-gradient-to-br from-primary-50 to-secondary-50',
    },
    
    // Text colors
    text: {
      primary: 'text-primary-600',
      primaryDark: 'text-primary-800',
      secondary: 'text-secondary-600',
      neutral: 'text-neutral-600',
      neutralDark: 'text-neutral-800',
      neutralLight: 'text-neutral-500',
      white: 'text-white',
      heading: 'text-neutral-800',
      body: 'text-neutral-600',
    },
    
    // Border colors
    borders: {
      primary: 'border-primary-200',
      secondary: 'border-secondary-200',
      neutral: 'border-neutral-200',
      light: 'border-neutral-100',
    },
    
    // Button styles
    buttons: {
      primary: `bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white ${currentTheme.borderRadius.full} ${currentTheme.shadows.medium} hover:${currentTheme.shadows.large} transition-all ${currentTheme.animations.duration}`,
      secondary: `bg-neutral-100 text-neutral-600 ${currentTheme.borderRadius.medium} hover:bg-neutral-200 transition-all ${currentTheme.animations.duration}`,
      outline: `border border-primary-200 text-primary-600 ${currentTheme.borderRadius.medium} hover:bg-primary-50 transition-all ${currentTheme.animations.duration}`,
    },
    
    // Card styles
    cards: {
      default: `bg-white/70 backdrop-blur-sm border border-primary-100 ${currentTheme.borderRadius.large} ${currentTheme.shadows.medium} hover:${currentTheme.shadows.large} hover:shadow-primary-100/50 transition-all duration-500`,
      simple: `bg-white border border-neutral-200 ${currentTheme.borderRadius.medium} ${currentTheme.shadows.small}`,
      elevated: `bg-white/60 backdrop-blur-sm border border-primary-100 ${currentTheme.borderRadius.large} ${currentTheme.shadows.large}`,
    },
    
    // Form elements
    inputs: {
      default: `px-4 py-3 border border-primary-200 ${currentTheme.borderRadius.medium} focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white/80 backdrop-blur-sm`,
      large: `px-6 py-4 border border-primary-200 ${currentTheme.borderRadius.large} focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white/80 backdrop-blur-sm`,
    },
    
    // Layout spacing
    spacing: {
      section: currentTheme.spacing.section,
      card: currentTheme.spacing.card,
      element: currentTheme.spacing.element,
    },
    
    // Typography
    typography: {
      heading: `${currentTheme.typography.headingWeight} text-neutral-800`,
      headingLarge: `text-4xl lg:text-5xl ${currentTheme.typography.headingWeight} text-neutral-800`,
      headingMedium: `text-2xl ${currentTheme.typography.headingWeight} text-neutral-800`,
      headingSmall: `text-xl ${currentTheme.typography.headingWeight} text-neutral-800`,
      body: `${currentTheme.typography.bodyWeight} text-neutral-600`,
      bodyLarge: `text-lg ${currentTheme.typography.bodyWeight} text-neutral-600`,
    },
    
    // Border radius
    radius: currentTheme.borderRadius,
    
    // Shadows
    shadows: currentTheme.shadows,
    
    // Animations
    animations: currentTheme.animations,
  };
};

// Utility function to get theme-aware Tailwind classes
export const themeClass = (baseClass: string, themeVariant?: string): string => {
  if (!themeVariant) return baseClass;
  
  // Replace color placeholders with theme-specific classes
  return baseClass
    .replace(/primary-(\d+)/g, `primary-$1`)
    .replace(/secondary-(\d+)/g, `secondary-$1`)
    .replace(/neutral-(\d+)/g, `neutral-$1`);
};

// Generate CSS for custom properties
export const generateThemeCSS = () => {
  return `
    :root {
      --color-primary-50: var(--color-primary-50);
      --color-primary-100: var(--color-primary-100);
      --color-primary-200: var(--color-primary-200);
      --color-primary-300: var(--color-primary-300);
      --color-primary-400: var(--color-primary-400);
      --color-primary-500: var(--color-primary-500);
      --color-primary-600: var(--color-primary-600);
      --color-primary-700: var(--color-primary-700);
      --color-primary-800: var(--color-primary-800);
      --color-primary-900: var(--color-primary-900);
      
      --color-secondary-50: var(--color-secondary-50);
      --color-secondary-100: var(--color-secondary-100);
      --color-secondary-200: var(--color-secondary-200);
      --color-secondary-300: var(--color-secondary-300);
      --color-secondary-400: var(--color-secondary-400);
      --color-secondary-500: var(--color-secondary-500);
      --color-secondary-600: var(--color-secondary-600);
      --color-secondary-700: var(--color-secondary-700);
      --color-secondary-800: var(--color-secondary-800);
      --color-secondary-900: var(--color-secondary-900);
      
      --color-neutral-50: var(--color-neutral-50);
      --color-neutral-100: var(--color-neutral-100);
      --color-neutral-200: var(--color-neutral-200);
      --color-neutral-300: var(--color-neutral-300);
      --color-neutral-400: var(--color-neutral-400);
      --color-neutral-500: var(--color-neutral-500);
      --color-neutral-600: var(--color-neutral-600);
      --color-neutral-700: var(--color-neutral-700);
      --color-neutral-800: var(--color-neutral-800);
      --color-neutral-900: var(--color-neutral-900);
    }
  `;
};